import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";

import { fetchReengagementWaveEmails } from "./reengagementEmailsReactQuery";
import {
  BLUE,
  BOOKED_HELP_TEXT,
  CLAY,
  DCMP_ENCOUNTER_HELP_TEXT,
  GOLD,
  GREEN,
  MetricCard,
  MetricCardGrid,
  MetricLabel,
  PLUM,
  Section,
  fmtDate,
  fmtDateTime,
  fmtInt,
  fmtPct,
  hasVisibleGroupData,
  pct,
  toNumber,
} from "./reengagementEmailsShared";
import type {
  ReengagementEmailFilters,
  ReengagementEmailPage,
  ReengagementWave,
  ReengagementWaveGroup,
} from "./reengagementEmailsTypes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function waveChartData(groups: ReengagementWaveGroup[]) {
  return {
    labels: groups.map((row) => row.label),
    datasets: [
      {
        label: "Emails Sent",
        data: groups.map((row) => toNumber(row.emailsSent)),
        backgroundColor: PLUM,
        borderRadius: 4,
      },
      {
        label: "Carespaces Sent",
        data: groups.map((row) => toNumber(row.carespacesSent)),
        backgroundColor: BLUE,
        borderRadius: 4,
      },
      {
        label: "Replied",
        data: groups.map((row) => toNumber(row.repliedCount)),
        backgroundColor: GREEN,
        borderRadius: 4,
      },
      {
        label: "Booked",
        data: groups.map((row) => toNumber(row.bookedCount)),
        backgroundColor: GOLD,
        borderRadius: 4,
      },
      {
        label: "DCMP Encounter",
        data: groups.map((row) => toNumber(row.encounteredCount)),
        backgroundColor: CLAY,
        borderRadius: 4,
      },
    ],
  };
}

function EmailFilter({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options?: Array<{ label: string; value: string }>;
  value: string;
}) {
  const choices = options || [
    { value: "all", label: "All" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  return (
    <label className="flex flex-col gap-1 text-xs font-bold text-muted-foreground">
      {label}
      <select
        aria-label={`Filter by ${label}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-[125px] rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
        style={{ minWidth: label === "Outreach group" ? 230 : 125 }}
      >
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Outcome({ value }: { value: boolean }) {
  return (
    <span className={value ? "font-bold text-emerald-700" : "text-muted-foreground"}>
      {value ? "Yes" : "No"}
    </span>
  );
}

function EmailTable({
  emailPage,
  loading,
  onPageChange,
}: {
  emailPage: ReengagementEmailPage;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  const page = toNumber(emailPage?.page) || 1;
  const pageSize = toNumber(emailPage?.pageSize) || 50;
  const totalCount = toNumber(emailPage?.totalCount);
  const rows = emailPage?.rows || [];
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const firstRow = totalCount ? (page - 1) * pageSize + 1 : 0;
  const lastRow = Math.min(page * pageSize, totalCount);

  return (
    <div aria-busy={loading}>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-2 py-2.5">Carespace ID</th>
                <th className="px-2 py-2.5">Time sent</th>
                <th className="px-2 py-2.5">Group</th>
                <th className="px-2 py-2.5">Message</th>
                <th className="px-2 py-2.5">Delivered</th>
                <th className="px-2 py-2.5">Delivery issues</th>
                <th className="px-2 py-2.5">Replied</th>
                <th className="px-2 py-2.5">Replies</th>
                <th className="px-2 py-2.5">Booked</th>
                <th className="px-2 py-2.5">DCMP encounter</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.activityId} className="border-b border-border/60 align-top">
                  <td className="min-w-[200px] px-2 py-2.5">
                    <a
                      href={`https://app.craniometrix.com/carespaces/${row.carespaceId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#0081AF] underline-offset-2 hover:underline"
                    >
                      {row.carespaceId}
                    </a>
                  </td>
                  <td className="min-w-[150px] px-2 py-2.5">{fmtDateTime(row.sentAt)}</td>
                  <td className="min-w-[180px] px-2 py-2.5">{row.groupLabel}</td>
                  <td className="min-w-[360px] max-w-[520px] px-2 py-2.5">
                    <div className="max-h-40 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                      {row.message || "—"}
                    </div>
                  </td>
                  <td className="px-2 py-2.5">
                    <Outcome value={row.delivered} />
                  </td>
                  <td className="px-2 py-2.5">
                    <Outcome value={row.deliveryIssue} />
                  </td>
                  <td className="px-2 py-2.5">
                    <Outcome value={row.replied} />
                  </td>
                  <td className="min-w-[320px] max-w-[460px] px-2 py-2.5">
                    {row.replies?.length ? (
                      <div className="flex max-h-48 flex-col gap-2.5 overflow-y-auto">
                        {row.replies.map((reply) => (
                          <div
                            key={`${row.activityId}-reply-${reply.sentAt}-${reply.message}`}
                            className="leading-relaxed"
                          >
                            <div className="mb-0.5 text-[0.72rem] text-muted-foreground">
                              {fmtDateTime(reply.sentAt)}
                            </div>
                            <div className="whitespace-pre-wrap">
                              {reply.message || "Reply message unavailable"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    <Outcome value={row.booked} />
                  </td>
                  <td className="px-2 py-2.5">
                    <Outcome value={row.encountered} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="m-0 text-sm text-muted-foreground">No emails match the selected filters.</p>
      )}

      {totalCount ? (
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            Showing {fmtInt(firstRow)}–{fmtInt(lastRow)} of {fmtInt(totalCount)} emails
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={loading || page === 1}
              onClick={() => onPageChange(page - 1)}
              className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-foreground">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={loading || page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ReengagementWaveSection({
  month,
  outcomesObservedThrough,
  wave,
}: {
  month: string;
  outcomesObservedThrough: string;
  wave: ReengagementWave;
}) {
  const [emailsOpen, setEmailsOpen] = useState(false);
  const [emailPage, setEmailPage] = useState(1);
  const [emailData, setEmailData] = useState<ReengagementEmailPage | null>(null);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState("");
  const [emailRetryVersion, setEmailRetryVersion] = useState(0);
  const displayedEmailPageRef = useRef(1);
  const [emailFilters, setEmailFilters] = useState<ReengagementEmailFilters>({
    outreachGroup: "all",
    replied: "all",
    booked: "all",
    encountered: "all",
  });
  const visibleGroups = useMemo(() => wave.groups.filter(hasVisibleGroupData), [wave.groups]);
  const chartData = useMemo(() => waveChartData(visibleGroups), [visibleGroups]);
  const chartOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: "bottom" },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0 },
        },
        x: {
          grid: { display: false },
        },
      },
    }),
    [],
  );

  const hasUnclassified = visibleGroups.some(
    (row) => row.outreachGroup === "unclassified" && toNumber(row.emailsSent) > 0,
  );

  useEffect(() => {
    if (!emailsOpen) return undefined;
    void emailRetryVersion;

    const controller = new AbortController();
    let ignore = false;
    setEmailsLoading(true);
    setEmailsError("");

    fetchReengagementWaveEmails(month, wave.waveDate, emailPage, emailFilters, {
      signal: controller.signal,
    })
      .then((nextData) => {
        if (ignore) return;
        displayedEmailPageRef.current = nextData.emailPage.page;
        setEmailData(nextData.emailPage);
      })
      .catch((error: Error) => {
        if (ignore || error?.name === "AbortError") return;
        setEmailsError(error.message || "Failed to load emails for this wave");
        setEmailPage(displayedEmailPageRef.current);
      })
      .finally(() => {
        if (!ignore) setEmailsLoading(false);
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [emailFilters, emailPage, emailRetryVersion, emailsOpen, month, wave.waveDate]);

  function updateEmailFilter(key: keyof ReengagementEmailFilters, value: string) {
    setEmailPage(1);
    displayedEmailPageRef.current = 1;
    setEmailData(null);
    setEmailFilters((filters) => ({ ...filters, [key]: value }));
  }

  return (
    <Section
      title={`Wave ${wave.waveNumber} · ${fmtDate(wave.waveDate)}`}
      subtitle={`Observed send date. Outcomes are counted after this wave through ${fmtDate(outcomesObservedThrough)}.`}
    >
      <MetricCardGrid className="mb-4 grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Emails Sent"
          value={fmtInt(wave.totals.emailsSent)}
          sub={`${fmtInt(wave.totals.carespacesSent)} distinct carespaces in this wave`}
          color={PLUM}
          face="#F2E8F1"
        />
        <MetricCard
          label="Replied"
          value={fmtInt(wave.totals.repliedCount)}
          sub={`${fmtPct(pct(wave.totals.repliedCount, wave.totals.carespacesSent))} of wave carespaces`}
          color={GREEN}
          face="#E6F5F2"
        />
        <MetricCard
          label="Booked"
          helpText={BOOKED_HELP_TEXT}
          value={fmtInt(wave.totals.bookedCount)}
          sub={`${fmtPct(pct(wave.totals.bookedCount, wave.totals.carespacesSent))} of wave carespaces`}
          color={GOLD}
          face="#F7EED7"
        />
        <MetricCard
          label="DCMP Encounter"
          helpText={DCMP_ENCOUNTER_HELP_TEXT}
          value={fmtInt(wave.totals.encounteredCount)}
          sub={`${fmtPct(pct(wave.totals.encounteredCount, wave.totals.carespacesSent))} of wave carespaces`}
          color={CLAY}
          face="#F8E1DC"
        />
      </MetricCardGrid>

      <div className="relative mb-4 h-80">
        <Bar data={chartData} options={chartOptions} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-2 py-2.5">Outreach group</th>
              <th className="px-2 py-2.5">Carespaces emailed</th>
              <th className="px-2 py-2.5">Emails sent</th>
              <th className="px-2 py-2.5">Delivered</th>
              <th className="px-2 py-2.5">Delivery issues</th>
              <th className="px-2 py-2.5">Replied</th>
              <th className="px-2 py-2.5">
                <MetricLabel label="Booked" helpText={BOOKED_HELP_TEXT} />
              </th>
              <th className="px-2 py-2.5">
                <MetricLabel label="DCMP encounter" helpText={DCMP_ENCOUNTER_HELP_TEXT} />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map((row) => (
              <tr
                key={`${wave.waveDate}-${row.outreachGroup}`}
                className="border-b border-border/60"
              >
                <td className="px-2 py-2.5 font-semibold text-foreground">{row.label}</td>
                <td className="px-2 py-2.5">{fmtInt(row.carespacesSent)}</td>
                <td className="px-2 py-2.5">{fmtInt(row.emailsSent)}</td>
                <td className="px-2 py-2.5">{fmtInt(row.deliveredCount)}</td>
                <td className="px-2 py-2.5">{fmtInt(row.deliveryIssueCount)}</td>
                <td className="px-2 py-2.5">{fmtInt(row.repliedCount)}</td>
                <td className="px-2 py-2.5">{fmtInt(row.bookedCount)}</td>
                <td className="px-2 py-2.5">{fmtInt(row.encounteredCount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasUnclassified ? (
        <p className="mt-3.5 mb-0 text-xs leading-relaxed text-muted-foreground">
          This wave includes campaign sends without a persisted outreach-group tag. Those rows
          appear as <strong>Unclassified Legacy Sends</strong>.
        </p>
      ) : null}

      <div className="mt-4 border-t border-border pt-4">
        <button
          type="button"
          aria-expanded={emailsOpen}
          onClick={() => setEmailsOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-3 bg-transparent py-1 text-left text-sm font-bold text-foreground"
        >
          <span>Emails in this wave ({fmtInt(wave.totals.emailsSent)})</span>
          <span aria-hidden="true" className="text-muted-foreground">
            {emailsOpen ? "−" : "+"}
          </span>
        </button>

        {emailsOpen ? (
          <div className="mt-3.5">
            <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md bg-muted/40 p-3.5">
              <EmailFilter
                label="Outreach group"
                value={emailFilters.outreachGroup}
                onChange={(value) => updateEmailFilter("outreachGroup", value)}
                options={[
                  { value: "all", label: "All groups" },
                  ...visibleGroups.map((group) => ({
                    value: group.outreachGroup,
                    label: group.label,
                  })),
                ]}
              />
              <EmailFilter
                label="Replied"
                value={emailFilters.replied}
                onChange={(value) => updateEmailFilter("replied", value)}
              />
              <EmailFilter
                label="Booked"
                value={emailFilters.booked}
                onChange={(value) => updateEmailFilter("booked", value)}
              />
              <EmailFilter
                label="DCMP encounter"
                value={emailFilters.encountered}
                onChange={(value) => updateEmailFilter("encountered", value)}
              />
            </div>
            {emailsLoading && !emailData ? (
              <p className="m-0 text-sm text-muted-foreground">Loading emails…</p>
            ) : null}
            {emailsError ? (
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="m-0 text-sm text-red-700">{emailsError}</p>
                <button
                  type="button"
                  onClick={() => setEmailRetryVersion((version) => version + 1)}
                  className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
                >
                  Retry
                </button>
              </div>
            ) : null}
            {emailData ? (
              <EmailTable
                emailPage={emailData}
                loading={emailsLoading}
                onPageChange={setEmailPage}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Section>
  );
}
